import type {
  MidiChannel,
  MidiControlChange,
  MidiInstrument,
  MidiNote,
  MidiPitchBend,
  MidiProgramChange,
  MidiTrack,
} from "@midi-fantom-utils/midi-core";

/**
 * Control Changes, которые нужно оставить в финальном файле по умолчанию.
 *
 * 1  — Modulation
 * 7  — Volume
 * 10 — Pan
 * 11 — Expression
 * 64 — Sustain Pedal
 * 91 — Reverb Send
 * 93 — Chorus Send
 */
export const ALLOWED_CONTROL_CHANGES = [1, 7, 10, 11, 64, 91, 93] as const;

export const createEmptyTrack = (channel: MidiChannel = 0): MidiTrack => ({
  name: "",
  channel,
  instrument: {
    family: "piano",
    number: 0,
    name: "acoustic grand piano",
    percussion: false,
  },
  notes: [],
  controlChanges: new Map(),
  programChanges: [],
  pitchBends: [],
  sysex: [],
  meta: [],
  unknownMeta: [],
});

export const cloneInstrument = (i: MidiInstrument): MidiInstrument => ({
  family: i.family,
  number: i.number,
  name: i.name,
  percussion: i.percussion,
});

/**
 * Переводит трек на новый канал, переписывая per-event `channel` во всех
 * событиях (notes / controlChanges / programChanges / pitchBends), чтобы
 * in-memory состояние не противоречило `track.channel`. Используется в
 * `buildFantomTracks` при merge/place/перенумерации — иначе события сохраняют
 * исходные каналы, а `encodeMidi` / `buildPlaybackData` работают по ним,
 * создавая рассинхрон (issue #5).
 */
export const withChannel = (
  track: MidiTrack,
  channel: MidiChannel
): MidiTrack => {
  const notes: MidiNote[] = track.notes.map((n) => ({ ...n, channel }));
  const controlChanges = new Map<number, ReadonlyArray<MidiControlChange>>();
  for (const [number, list] of track.controlChanges) {
    controlChanges.set(number, list.map((c) => ({ ...c, channel })));
  }
  const programChanges: MidiProgramChange[] = track.programChanges.map(
    (pc) => ({ ...pc, channel })
  );
  const pitchBends: MidiPitchBend[] = track.pitchBends.map((pb) => ({
    ...pb,
    channel,
  }));
  return {
    ...track,
    channel,
    notes,
    controlChanges,
    programChanges,
    pitchBends,
  };
};